import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Phone, MessageCircle, Building2, User as UserIcon, LayoutGrid, List as ListIcon, Map as MapIcon, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { phoneToWa } from "../lib/contact";
import PhaseChip from "../components/PhaseChip";
import Modal from "../components/Modal";
import GenericBulkImportDialog from "../components/GenericBulkImportDialog";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { CUSTOMER_STATUSES } from "../lib/enums";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = usePersistedState("customers.q", "");
  const [typeFilter, setTypeFilter] = usePersistedState("customers.typeFilter", "all");
  const [statusFilter, setStatusFilter] = usePersistedState("customers.statusFilter", "all");
  const [lifecycleFilter, setLifecycleFilter] = usePersistedState("customers.lifecycleFilter", "all");
  const [view, setView] = usePersistedState("customers.view", "grid");
  const [importing, setImporting] = useState(false);
  const { user } = useAuth();

  const load = async (_opts) => {
    // Iter 173 — same data, no skeleton needed; this list isn't loading-gated.
    const { data } = await api.get("/lifecycle/customers");
    // [Iter 290zp.19] Defensive — never let a non-array response from the
    // API crash the page with "list.filter is not a function". A transient
    // proxy / hot-reload / 304 response can resolve as null or an object;
    // coerce to [] so the page stays alive.
    setList(Array.isArray(data) ? data : []);
  };
  useEffect(() => {
    load();
  }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });

  // [Iter 290zp.19] Defensive — guard against any path that leaves
  // `list` as a non-array. Setter is already array-coerced; this is a
  // belt-and-braces second line of defence.
  const safeList = Array.isArray(list) ? list : [];
  const filtered = safeList.filter((c) => {
    if (typeFilter !== "all" && (c.customer_type || "individual") !== typeFilter) return false;
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (lifecycleFilter === "lead" && !c.has_active_lead) return false;
    if (lifecycleFilter === "jobcard" && !c.has_open_jobcard) return false;
    if (lifecycleFilter === "ticket" && !c.has_open_ticket) return false;
    if (lifecycleFilter === "idle" && (c.has_active_lead || c.has_open_jobcard || c.has_open_ticket)) return false;
    if (q) {
      const s = q.toLowerCase();
      if (
        !c.name.toLowerCase().includes(s) &&
        !(c.email || "").toLowerCase().includes(s) &&
        !(c.phone || "").includes(q) &&
        !(c.vat_number || "").toLowerCase().includes(s) &&
        !(c.external_ref || "").toLowerCase().includes(s)
      )
        return false;
    }
    return true;
  });

  // Auto-sort by status (active first), then by name
  const STATUS_ORDER = { lead: 0, active: 1, inactive: 2, closed: 3, lost: 4 };
  const sorted = [...filtered].sort(
    (a, b) =>
      (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
      a.name.localeCompare(b.name)
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Directory</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Customers
            <span className="text-sm text-emerald-300/80 font-normal ml-1">{list.length}</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex border border-white/10 rounded-md overflow-hidden">
            <button
              onClick={() => setView("grid")}
              data-testid="customers-view-grid"
              className={`px-3 py-2 text-xs ${
                view === "grid" ? "bg-emerald-500/15 text-emerald-300" : "text-[#647087] hover:text-white"
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="customers-view-list"
              className={`px-3 py-2 text-xs ${
                view === "list" ? "bg-emerald-500/15 text-emerald-300" : "text-[#647087] hover:text-white"
              }`}
              title="List view"
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>
          <Link
            to="/map"
            className="hv-btn hv-btn-ghost"
            data-testid="customers-map-button"
            title="Open all sites on the map"
          >
            <MapIcon className="w-4 h-4" /> Map
          </Link>
          {(user?.role === "owner" || user?.role === "admin") && (
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="hv-btn hv-btn-ghost"
              data-testid="customers-import-button"
              title="Import customers from xlsx/csv"
            >
              <Sparkles className="w-4 h-4 text-[#A78BFA]" /> Import
            </button>
          )}
          <Link
            to="/customers/new"
            className="hv-btn hv-btn-primary"
            data-testid="customers-new-button"
          >
            <Plus className="w-4 h-4" /> New customer
          </Link>
        </div>
      </div>

      <Modal open={importing} onClose={() => setImporting(false)} title="Import customers" testId="customers-import-modal" maxWidth="max-w-3xl">
        <GenericBulkImportDialog
          entity="customers"
          onDone={() => { setImporting(false); load(); }}
          onCancel={() => setImporting(false)}
        />
      </Modal>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="hv-input hv-input-search"
            placeholder="Search name, email, phone, VAT…"
            data-testid="customers-search"
          />
        </div>
        <FilterPills
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            ["all", "All"],
            ["company", "Companies"],
            ["individual", "Individuals"],
          ]}
          testidPrefix="customers-filter"
        />
        <FilterPills
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            ["all", "Any status"],
            ["lead", "Lead"],
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["closed", "Closed"],
            ["lost", "Lost"],
          ]}
          testidPrefix="customers-status"
        />
        <FilterPills
          value={lifecycleFilter}
          onChange={setLifecycleFilter}
          options={[
            ["all", "All lifecycle"],
            ["lead", "In lead cycle"],
            ["jobcard", "In job card"],
            ["ticket", "Has support ticket"],
            ["idle", "Idle"],
          ]}
          testidPrefix="customers-lifecycle"
        />
      </div>

      {view === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((c) => {
            const isCo = (c.customer_type || "individual") === "company";
            return (
              <Link
                to={`/customers/${c.id}`}
                key={c.id}
                data-testid={`customer-card-${c.id}`}
                className="hv-card p-4 hover:border-[#1fb6c1]/40 transition flex flex-col gap-3 min-w-0"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex items-start gap-2">
                    {isCo ? (
                      <Building2 className="w-5 h-5 text-[#1fb6c1] mt-0.5 flex-shrink-0" />
                    ) : (
                      <UserIcon className="w-5 h-5 text-[#a78bfa] mt-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-lg truncate">{c.name}</div>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {c.external_ref && (
                          <span
                            className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-[#1fb6c1]/30 text-[#1fb6c1] bg-[#1fb6c1]/5"
                            data-testid={`customer-external-ref-${c.id}`}
                          >
                            {c.external_ref}
                          </span>
                        )}
                        <div className="text-xs text-[#647087] truncate">{c.email || "—"}</div>
                      </div>
                      {isCo && c.vat_number && (
                        <div className="text-[10px] text-[#647087] mt-0.5 font-mono">VAT {c.vat_number}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PhaseChip value={c.status} />
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        isCo
                          ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                          : "border-[#a78bfa]/40 text-[#a78bfa] bg-[#a78bfa]/5"
                      }`}
                    >
                      {isCo ? "Company" : "Individual"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#a0aabf]">
                  {c.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {c.phone}
                    </span>
                  )}
                  {c.whatsapp && (
                    <span className="flex items-center gap-1 text-emerald-300/80">
                      <MessageCircle className="w-3 h-3" />
                      WhatsApp
                    </span>
                  )}
                </div>
                {c.google_rating && (
                  <div className="text-xs text-amber-300">
                    ★ {c.google_rating} · {c.google_reviews_count || 0} reviews
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {c.has_active_lead && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
                      Lead
                    </span>
                  )}
                  {c.has_open_jobcard && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/5">
                      Job
                    </span>
                  )}
                  {c.has_open_ticket && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/5">
                      Ticket
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
          {sorted.length === 0 && (
            <div className="hv-card p-8 text-center text-[#647087]">No customers.</div>
          )}
        </div>
      ) : (
        <>
        {/* Mobile card list — clean compact rows that don't fall apart at
            narrow widths (the desktop table wrapped LIFE/CYC/LE on phones,
            user feedback 2026-02 #72). */}
        <div className="md:hidden space-y-2" data-testid="customers-mobile-list">
          {sorted.map((c) => {
            const isCo = (c.customer_type || "individual") === "company";
            return (
              <Link
                key={c.id}
                to={`/customers/${c.id}`}
                className="hv-card p-3 block hover:border-emerald-400/40 transition"
                data-testid={`customer-row-mobile-${c.id}`}
              >
                <div className="flex items-start gap-2">
                  {isCo ? <Building2 className="w-4 h-4 text-[#1fb6c1] mt-0.5 shrink-0" /> : <UserIcon className="w-4 h-4 text-[#a78bfa] mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{c.name}</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {c.external_ref && (
                        <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-[#1fb6c1]/30 text-[#1fb6c1] bg-[#1fb6c1]/5">
                          {c.external_ref}
                        </span>
                      )}
                      <span
                        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                          isCo
                            ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                            : "border-[#a78bfa]/40 text-[#a78bfa] bg-[#a78bfa]/5"
                        }`}
                      >
                        {isCo ? "Company" : "Individual"}
                      </span>
                      <PhaseChip value={c.status} />
                      {c.has_active_lead && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">Lead</span>
                      )}
                      {c.has_open_jobcard && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/5">Job</span>
                      )}
                      {c.has_open_ticket && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/5">Ticket</span>
                      )}
                    </div>
                    {(c.email || c.phone) && (
                      <div className="text-[11px] text-[#647087] mt-1 truncate">
                        {c.email}{c.email && c.phone ? " · " : ""}{c.phone}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
          {sorted.length === 0 && <div className="hv-card p-6 text-center text-[#647087] text-sm">No customers.</div>}
        </div>

        {/* Desktop table view */}
        <div className="hv-card overflow-hidden hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#0a0b0e] border-b border-white/10">
              <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Lifecycle</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const isCo = (c.customer_type || "individual") === "company";
                return (
                  <tr
                    key={c.id}
                    className="border-b border-white/5 hover:bg-emerald-500/5 transition"
                    data-testid={`customer-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <Link to={`/customers/${c.id}`} className="font-medium hover:text-emerald-300 transition flex items-center gap-2">
                        {isCo ? <Building2 className="w-4 h-4 text-[#1fb6c1]" /> : <UserIcon className="w-4 h-4 text-[#a78bfa]" />}
                        {c.name}
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        {c.external_ref && (
                          <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-[#1fb6c1]/30 text-[#1fb6c1] bg-[#1fb6c1]/5">
                            {c.external_ref}
                          </span>
                        )}
                        {c.email && <div className="text-xs text-[#647087]">{c.email}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#a0aabf]">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                          isCo
                            ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                            : "border-[#a78bfa]/40 text-[#a78bfa] bg-[#a78bfa]/5"
                        }`}
                      >
                        {isCo ? "Company" : "Individual"}
                      </span>
                      {isCo && c.vat_number && (
                        <span className="ml-2 text-[10px] text-[#647087] font-mono">VAT {c.vat_number}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#a0aabf]">
                      <div className="flex items-center gap-2 text-xs">
                        {c.phone && (
                          <a
                            href={phoneToWa(c.phone) || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition"
                            data-testid={`customer-call-${c.id}`}
                            title="Call (opens WhatsApp)"
                          >
                            <Phone className="w-3 h-3" />
                            {c.phone}
                          </a>
                        )}
                        {c.whatsapp && (
                          <a
                            href={`https://wa.me/${c.whatsapp.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 transition"
                            data-testid={`customer-wa-${c.id}`}
                            title="Open WhatsApp"
                          >
                            <MessageCircle className="w-3 h-3" />
                            WA
                          </a>
                        )}
                        {!c.phone && !c.whatsapp && <span className="text-[#647087]">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <PhaseChip value={c.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {c.has_active_lead && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
                            Lead
                          </span>
                        )}
                        {c.has_open_jobcard && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/5">
                            Job
                          </span>
                        )}
                        {c.has_open_ticket && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/5">
                            Ticket
                          </span>
                        )}
                        {!c.has_active_lead && !c.has_open_jobcard && !c.has_open_ticket && (
                          <span className="text-[10px] text-[#647087]">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {sorted.length === 0 && <div className="p-6 text-center text-[#647087]">No customers.</div>}
        </div>
        </>
      )}

    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</label>
      <input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="hv-input mt-1"
        data-testid={testid}
      />
    </div>
  );
}

function FilterPills({ value, onChange, options, testidPrefix }) {
  return (
    <div className="flex border border-white/10 rounded-md overflow-x-auto max-w-full no-scrollbar" data-testid={`${testidPrefix}-pills`}>
      {options.map(([k, l]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          data-testid={`${testidPrefix}-${k}`}
          className={`px-3 py-2 text-xs uppercase tracking-wider whitespace-nowrap shrink-0 ${
            value === k ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
